import type { Node, Edge } from '@xyflow/react';
import type {
  AssetGraphResponseDto,
  AssetRelationshipType,
  ExternalNetworkEntityKind,
} from '@terra/shared-contracts-external-network';
import {
  getRootEntryNodeIds,
  getRouteRootEntryNodeIds,
} from './getRootEntryNodeIds';
import { formatCloudProviderLabel } from '@terra/shared-contracts-integrations';
import {
  CLOUD_ACCOUNT_NODE_ID_PREFIX,
  INTERNET_NODE_ID,
} from '../flow-layout/constants';
import { SYNTHETIC_EDGE_TYPES, type AssetNodeData } from './constants';
import {
  buildVantagePointRoot,
  INTERNET_VANTAGE_POINT,
  type ExternalNetworkGraphVantagePoint,
} from './graph-vantage-point';
import {
  appendApplicationNodes,
  type GraphWebAppCluster,
} from './buildFlowApplicationNodes';
import { orientFlowEdgesFromRoot } from './orientFlowEdgesFromRoot';
import { getOpenPortsByNodeId } from './getOpenPortsByNodeId';

type AssetGraphNode = AssetGraphResponseDto['nodes'][number];
type AssetGraphEdge = AssetGraphResponseDto['edges'][number];

export type { GraphWebAppCluster } from './buildFlowApplicationNodes';

type BuildFlowElementsOptions = {
  includeInternetNode?: boolean;
  edgeMode?: 'all' | 'primary';
  webAppClusters?: GraphWebAppCluster[];
  selectedNodeId?: string;
  orientEdgesFromInternet?: boolean;
  vantagePoint?: ExternalNetworkGraphVantagePoint;
};
type CloudAccountGroup = {
  id: string;
  label: string;
  cloudProvider: string;
  storageNodeIds: string[];
};

function getCloudAccountGroups(
  graph: AssetGraphResponseDto,
): CloudAccountGroup[] {
  const groupsById = new Map<string, CloudAccountGroup>();

  for (const node of graph.nodes) {
    const { cloudProvider, cloudAccountRef } = node.properties;

    if (node.kind !== 'STORAGE_SERVICE' || !cloudProvider || !cloudAccountRef) {
      continue;
    }

    const id = `${CLOUD_ACCOUNT_NODE_ID_PREFIX}${cloudProvider}:${cloudAccountRef}`;
    const existing = groupsById.get(id);

    if (existing) {
      existing.storageNodeIds.push(node.id);
      continue;
    }

    groupsById.set(id, {
      id,
      label: `${formatCloudProviderLabel({ provider: cloudProvider })} · ${cloudAccountRef}`,
      cloudProvider,
      storageNodeIds: [node.id],
    });
  }

  return [...groupsById.values()];
}

function createPrimaryGraphEdge({
  source,
  target,
  type,
}: {
  source: string;
  target: string;
  type: AssetGraphEdge['type'];
}): AssetGraphEdge {
  return {
    id: `primary-${source}-${target}-${type}`,
    source,
    target,
    type,
  };
}

function getConnectedIpIdsByNodeId({
  nodesById,
  edges,
}: {
  nodesById: Map<string, AssetGraphNode>;
  edges: AssetGraphEdge[];
}) {
  const connectedIpIdsByNodeId = new Map<string, Set<string>>();

  for (const edge of edges) {
    const sourceNode = nodesById.get(edge.source);
    const targetNode = nodesById.get(edge.target);

    if (!sourceNode || !targetNode) continue;

    if (sourceNode.kind === 'HOST' && targetNode.kind !== 'HOST') {
      const ipIds =
        connectedIpIdsByNodeId.get(targetNode.id) ?? new Set<string>();

      ipIds.add(sourceNode.id);
      connectedIpIdsByNodeId.set(targetNode.id, ipIds);
    }

    if (targetNode.kind === 'HOST' && sourceNode.kind !== 'HOST') {
      const ipIds =
        connectedIpIdsByNodeId.get(sourceNode.id) ?? new Set<string>();

      ipIds.add(targetNode.id);
      connectedIpIdsByNodeId.set(sourceNode.id, ipIds);
    }
  }

  return connectedIpIdsByNodeId;
}

function getConnectedServiceIdsByNodeId({
  nodesById,
  edges,
}: {
  nodesById: Map<string, AssetGraphNode>;
  edges: AssetGraphEdge[];
}) {
  const connectedServiceIdsByNodeId = new Map<string, Set<string>>();

  for (const edge of edges) {
    const sourceNode = nodesById.get(edge.source);
    const targetNode = nodesById.get(edge.target);

    if (!sourceNode || !targetNode) continue;

    if (
      sourceNode.kind === 'SOFTWARE' &&
      targetNode.kind === 'NETWORK_SERVICE'
    ) {
      const serviceIds =
        connectedServiceIdsByNodeId.get(sourceNode.id) ?? new Set<string>();

      serviceIds.add(targetNode.id);
      connectedServiceIdsByNodeId.set(sourceNode.id, serviceIds);
    }

    if (
      targetNode.kind === 'SOFTWARE' &&
      sourceNode.kind === 'NETWORK_SERVICE'
    ) {
      const serviceIds =
        connectedServiceIdsByNodeId.get(targetNode.id) ?? new Set<string>();

      serviceIds.add(sourceNode.id);
      connectedServiceIdsByNodeId.set(targetNode.id, serviceIds);
    }
  }

  return connectedServiceIdsByNodeId;
}

function getRootIdByNodeId({
  graph,
  rootEntryNodeIds,
}: {
  graph: AssetGraphResponseDto;
  rootEntryNodeIds: Set<string>;
}) {
  const adjacency = new Map<string, string[]>();
  const rootIdByNodeId = new Map<string, string>();

  for (const edge of graph.edges) {
    const sourceNeighbors = adjacency.get(edge.source) ?? [];

    sourceNeighbors.push(edge.target);
    adjacency.set(edge.source, sourceNeighbors);

    const targetNeighbors = adjacency.get(edge.target) ?? [];

    targetNeighbors.push(edge.source);
    adjacency.set(edge.target, targetNeighbors);
  }

  for (const rootId of Array.from(rootEntryNodeIds).sort()) {
    const queue = [rootId];

    while (queue.length > 0) {
      const nodeId = queue.shift();

      if (!nodeId || rootIdByNodeId.has(nodeId)) continue;
      rootIdByNodeId.set(nodeId, rootId);

      for (const neighborId of adjacency.get(nodeId) ?? []) {
        if (!rootIdByNodeId.has(neighborId)) {
          queue.push(neighborId);
        }
      }
    }
  }

  for (const node of graph.nodes) {
    if (!rootIdByNodeId.has(node.id)) {
      rootIdByNodeId.set(node.id, node.id);
    }
  }

  return rootIdByNodeId;
}

function edgeTypeFromIpToNode(
  kind: ExternalNetworkEntityKind,
): AssetRelationshipType {
  switch (kind) {
    case 'NETWORK_SERVICE':
      return 'RUNS_SERVICE';
    case 'DOMAIN':
      return 'RESOLVES_TO';
    case 'SOFTWARE':
      return 'RUNS_SOFTWARE';
    default:
      return 'WITHIN_NETBLOCK';
  }
}

function getPrimaryGraphEdges({
  graph,
  rootEntryNodeIds,
}: {
  graph: AssetGraphResponseDto;
  rootEntryNodeIds: Set<string>;
}) {
  const nodesById = new Map(graph.nodes.map((node) => [node.id, node]));
  const rootIdByNodeId = getRootIdByNodeId({ graph, rootEntryNodeIds });
  const connectedIpIdsByNodeId = getConnectedIpIdsByNodeId({
    nodesById,
    edges: graph.edges,
  });
  const connectedServiceIdsByNodeId = getConnectedServiceIdsByNodeId({
    nodesById,
    edges: graph.edges,
  });
  const edges: AssetGraphEdge[] = [];
  const connectedNodeIds = new Set<string>();
  const nodesByRootId = new Map<string, AssetGraphNode[]>();

  for (const node of graph.nodes) {
    const rootId = rootIdByNodeId.get(node.id) ?? node.id;
    const rootNodes = nodesByRootId.get(rootId) ?? [];

    rootNodes.push(node);
    nodesByRootId.set(rootId, rootNodes);
  }

  const addEdge = ({
    source,
    target,
    type,
  }: {
    source: string;
    target: string;
    type: AssetGraphEdge['type'];
  }) => {
    if (source === target || connectedNodeIds.has(target)) return;
    edges.push(createPrimaryGraphEdge({ source, target, type }));
    connectedNodeIds.add(target);
  };

  for (const [rootId, rootNodes] of nodesByRootId.entries()) {
    const rootNode = nodesById.get(rootId);
    const rootIpIds = rootNodes
      .filter((node) => node.kind === 'HOST' && node.id !== rootId)
      .map((node) => node.id)
      .sort();

    if (rootNode?.kind !== 'HOST') {
      for (const ipId of rootIpIds) {
        addEdge({ source: rootId, target: ipId, type: 'RESOLVES_TO' });
      }
    }

    for (const node of [...rootNodes].sort((left, right) =>
      left.id.localeCompare(right.id),
    )) {
      if (
        node.id === rootId ||
        node.kind === 'HOST' ||
        node.kind === 'WEAKNESS'
      ) {
        continue;
      }

      if (node.kind === 'SOFTWARE') {
        const connectedServiceId = Array.from(
          connectedServiceIdsByNodeId.get(node.id) ?? [],
        )
          .filter((serviceId) => rootIdByNodeId.get(serviceId) === rootId)
          .sort()[0];

        if (connectedServiceId) {
          addEdge({
            source: connectedServiceId,
            target: node.id,
            type: 'RUNS_SOFTWARE',
          });
          continue;
        }

        const connectedIpId = Array.from(
          connectedIpIdsByNodeId.get(node.id) ?? [],
        )
          .filter((ipId) => rootIdByNodeId.get(ipId) === rootId)
          .sort()[0];

        if (connectedIpId) {
          addEdge({
            source: connectedIpId,
            target: node.id,
            type: 'RUNS_SOFTWARE',
          });
        }

        continue;
      }

      const connectedIpId = Array.from(
        connectedIpIdsByNodeId.get(node.id) ?? [],
      )
        .filter((ipId) => rootIdByNodeId.get(ipId) === rootId)
        .sort()[0];

      if (connectedIpId) {
        addEdge({
          source: connectedIpId,
          target: node.id,
          type: edgeTypeFromIpToNode(node.kind),
        });
        continue;
      }

      addEdge({
        source: rootId,
        target: node.id,
        type: node.kind === 'DOMAIN' ? 'IS_SUBDOMAIN_OF' : 'WITHIN_NETBLOCK',
      });
    }
  }

  return edges;
}

export function buildFlowElements(
  graph: AssetGraphResponseDto,
  translateEdgeType: (edgeType: string) => string,
  {
    includeInternetNode = true,
    edgeMode = 'all',
    webAppClusters = [],
    selectedNodeId,
    orientEdgesFromInternet,
    vantagePoint = INTERNET_VANTAGE_POINT,
  }: BuildFlowElementsOptions = {},
) {
  const flowNodes: Node<AssetNodeData>[] = [];
  const flowEdges: Edge[] = [];
  const simplifyEdges = graph.nodes.length > 200;
  const showEdgeLabels = graph.nodes.length <= 80;
  const rootEntryNodeIds = orientEdgesFromInternet
    ? getRouteRootEntryNodeIds(graph)
    : getRootEntryNodeIds(graph);
  const root = buildVantagePointRoot(vantagePoint);

  if (includeInternetNode) {
    flowNodes.push({
      id: INTERNET_NODE_ID,
      type: 'externalNetworkNode',
      position: { x: 0, y: 0 },
      data: root.data,
    });

    for (const domainId of rootEntryNodeIds) {
      flowEdges.push({
        id: `internet-${domainId}`,
        source: INTERNET_NODE_ID,
        target: domainId,
        type: 'externalNetworkEdge',
        label: translateEdgeType(root.edgeType),
        animated: true,
        data: { edgeType: root.edgeType },
      });
    }
  }

  for (const group of getCloudAccountGroups(graph)) {
    flowNodes.push({
      id: group.id,
      type: 'externalNetworkNode',
      width: 180,
      height: 110,
      position: { x: 0, y: 0 },
      data: {
        label: group.label,
        entityKind: 'CLOUD_ACCOUNT',
        signalsCount: 0,
        cloudProvider: group.cloudProvider,
      } satisfies AssetNodeData,
    });

    if (includeInternetNode) {
      flowEdges.push({
        id: `internet-${group.id}`,
        source: INTERNET_NODE_ID,
        target: group.id,
        type: 'externalNetworkEdge',
        label: translateEdgeType(root.edgeType),
        animated: true,
        data: { edgeType: root.edgeType },
      });
    }

    for (const storageNodeId of group.storageNodeIds) {
      flowEdges.push({
        id: `${group.id}-${storageNodeId}`,
        source: group.id,
        target: storageNodeId,
        type: 'externalNetworkEdge',
        label: showEdgeLabels
          ? translateEdgeType(SYNTHETIC_EDGE_TYPES.IN_CLOUD_ACCOUNT)
          : undefined,
        animated: false,
        data: { edgeType: SYNTHETIC_EDGE_TYPES.IN_CLOUD_ACCOUNT },
      });
    }
  }

  const openPortsByNodeId = getOpenPortsByNodeId(graph);

  for (const node of graph.nodes) {
    flowNodes.push({
      id: node.id,
      type: 'externalNetworkNode',
      width: 180,
      height: 110,
      position: { x: 0, y: 0 },
      data: {
        label: node.label,
        entityKind: node.kind,
        nodeType: node.nodeType ?? 'asset',
        signalsCount: node.properties.signalsCount ?? 0,
        scope: node.properties.scope,
        cidr: node.properties.cidr,
        fqdn: node.properties.fqdn,
        ipAddress: node.properties.ipAddress,
        port: node.properties.port,
        openPorts: openPortsByNodeId.get(node.id),
        transportProtocol: node.properties.transportProtocol,
        applicationProtocol: node.properties.applicationProtocol,
        softwareType: node.properties.softwareType,
        vendor: node.properties.vendor,
        product: node.properties.product,
        version: node.properties.version,
        cloudProvider: node.properties.cloudProvider,
        cloudAccountRef: node.properties.cloudAccountRef,
        publicAccess: node.properties.publicAccess,
        websiteEnabled: node.properties.websiteEnabled,
      } satisfies AssetNodeData,
    });
  }

  const graphEdges =
    edgeMode === 'primary'
      ? getPrimaryGraphEdges({ graph, rootEntryNodeIds })
      : graph.edges;

  for (const edge of graphEdges) {
    flowEdges.push({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      type: 'externalNetworkEdge',
      label: showEdgeLabels ? translateEdgeType(edge.type) : undefined,
      animated: !simplifyEdges && edge.type === 'RESOLVES_TO',
      data: { edgeType: edge.type, simplified: simplifyEdges, showEdgeLabels },
    });
  }

  appendApplicationNodes({
    graph,
    clusters: webAppClusters,
    flowNodes,
    flowEdges,
    translateEdgeType,
    showEdgeLabels,
  });

  if (selectedNodeId) {
    for (const node of flowNodes) {
      node.selected = node.id === selectedNodeId;
    }
  }

  if (orientEdgesFromInternet && includeInternetNode) {
    return {
      flowNodes,
      flowEdges: orientFlowEdgesFromRoot({
        rootId: INTERNET_NODE_ID,
        edges: flowEdges,
      }),
    };
  }

  return { flowNodes, flowEdges };
}
